/**
 * Thrivia Embed SDK: the seam between a card and the notify-me capture.
 *
 * The capture is the biggest thing in the bundle that most page views never render: it is a form,
 * its validation, its consent copy and its POST, and it appears only when a visitor taps the
 * trigger on a card that is sold out or ended. Under ruling R52's 10 KiB cap that is the wrong
 * trade for every organizer whose events are on sale, so `capture.ts` now ships as its own
 * artifact and is fetched the first time a card actually wants one.
 *
 * This module is what stays behind: the types the card needs at build time, and a call that asks
 * for the chunk instead of containing it. It exists so that `card.ts` has something to import that
 * is NOT `capture.ts`, because a single static import is all it takes to pull the whole file back
 * into the core bundle.
 *
 * THE CARD DOES NOT MOVE WHEN THE CHUNK LANDS. The trigger occupies the box the low-spots chip
 * would have used, which the card body's 186px min-height already reserves (`styles.ts` carries
 * the arithmetic, and `render.test.ts` pins the two sums against each other). So a trigger that
 * appears a moment after the card does costs a repaint of the slot and no layout shift on the
 * organizer's page - which is the property that makes deferring it acceptable at all.
 */

/** What the capture needs from the mount that owns the card. Defined here so `card.ts` can see it. */
export interface CaptureContext {
  /** The attribution session this card's data arrived under, or null. */
  sid: string | null;
  /**
   * The surface of the mount the visitor is actually submitting from (CO-6).
   *
   * Typed as a plain string rather than `MountMode` on purpose, and the purpose is the fifth
   * surface: the backend validates this against its own `ALLOWED_SURFACES` and falls back to the
   * sid's surface for anything it does not know, so a loader that names a surface the deployed
   * backend has not learned yet degrades to today's behaviour rather than to a broken lead.
   */
  surface: string;
  /** True when this mount sends no beacons and must write no leads. */
  preview: boolean;
}

/** The capture chunk's entry point, once it has arrived. */
export type CaptureMounter = (
  slot: HTMLElement,
  body: HTMLElement,
  eventId: string,
  ctx: CaptureContext,
) => void;

let impl: CaptureMounter | null = null;
let want: (() => void) | null = null;

/** Called once at boot by `index.ts` with the thing that fetches the chunk. */
export function onCaptureWanted(fn: () => void): void {
  want = fn;
}

/** Called by the chunk itself, through `window.__thrivia.useCapture`. */
export function registerCapture(fn: CaptureMounter): void {
  impl = fn;
}

/**
 * Draws the capture trigger, or asks for the code that draws it.
 *
 * The first sold-out card on a page gets no trigger and starts the fetch; `index.ts` repaints
 * every card mount when the chunk registers, so it gets one a moment later. Every card after that
 * is served straight from `impl`.
 */
export function mountCapture(
  slot: HTMLElement,
  body: HTMLElement,
  eventId: string,
  ctx: CaptureContext,
): void {
  if (impl) {
    impl(slot, body, eventId, ctx);
    return;
  }
  if (want) want();
}
